import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import {
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Select,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	DatePickerField,
	Form,
	GetFieldValue,
	PhoneInputField,
	PhoneNumberValidator,
	SelectField,
	SelectInputField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import React, { useCallback, useMemo, memo } from "react"
import * as Validator from "yup"
import {
	TTouristProfile,
	FLIGHT_CATEGORY_CLASSES,
	TSmoking,
	SMOKING_PREFERENCES,
	TPreference,
} from "./store"
import {
	AddressInputField,
	TAddressInputFieldValue,
	transformAddressToStorableValue,
} from "../Addresses"
import { ILocation } from "../Locations"
import { SelectWorldCountries, TWorldAirport } from "../World"
import { ITag, SelectTags } from "../Tags"
import { SelectAirports } from "../Flights/List"
import { SelectPreferences } from "./List"
import { addLabelToAirport } from "../Flights"

export type TTouristProfileFormData = {
	id: number | null
	type?: "adult" | "child"
	salutation?: TTouristProfile["salutation"]
	name: string
	dob?: Date
	nationality?: ILocation["country"]
	phone_numbers?: Array<{
		country_code: string
		phone_number: string
		number: string
		is_primary?: boolean
		has_whatsapp?: boolean
	}>
	email?: string
	address?: TAddressInputFieldValue

	tourist_id?: number | null
	meal_preference: TPreference | undefined
	stay_preference: TPreference | undefined
	language_preference?: TPreference | undefined
	preferred_airport?: TWorldAirport
	preferred_flight_class?: string
	marriage_anniversary?: string
	is_smoker?: TSmoking | null
	tags?: Array<ITag>
	passport_number?: string
	passport_expiry_date?: Date
	company_name?: string
	gst_number?: string
	remarks?: string
}

const adultSalutations = ["Mr.", "Mrs.", "Ms."]
const childSalutations = ["Master", "Miss"]

export function NewTouristProfile({
	onSuccess,
	onCancel,
	...props
}: {
	onSuccess: (touristProfile: TTouristProfile) => void
	onCancel?: () => void
} & Omit<
	React.ComponentProps<typeof TouristProfileBasicInfoItem>,
	"onSubmit"
>) {
	const xhr = useXHR()
	return (
		<>
			<TouristProfileForm
				onSubmit={async (values: unknown) => {
					const touristProfile = await xhr.post("/tourist-profiles", values)
					showSnackbar("New profile added successfully.")
					onSuccess(touristProfile.data.data)
					return touristProfile
				}}
				onCancel={onCancel}
				{...props}
			/>
		</>
	)
}

export function TouristProfileForm({
	initialValues: propInitialValues = {},
	hideBasicInfo,
	onSubmit,
	onCancel,
}: {
	initialValues?: Partial<TTouristProfileFormData>
	hideBasicInfo?: boolean
	onSubmit: (data: unknown) => Promise<unknown>
	onCancel?: () => void
}) {
	const computedInitialValues = useMemo<TTouristProfileFormData>(() => {
		const {
			id,
			type,
			salutation,
			name,
			dob,
			nationality,
			phone_numbers,
			email,
			address,

			tourist_id,
			meal_preference,
			stay_preference,
			language_preference,
			preferred_airport,
			preferred_flight_class,
			marriage_anniversary,
			is_smoker,
			tags,
			passport_number,
			passport_expiry_date,
			company_name,
			gst_number,
			remarks,
		} = propInitialValues

		return {
			id: id ?? null,
			type: type ?? "adult",
			salutation: salutation ?? undefined,
			name: name ?? "",
			dob: dob ? parseDate(dob) : undefined,
			nationality: nationality ?? undefined,
			phone_numbers: phone_numbers
				? phone_numbers.map((p) => ({
						country_code: p.country_code || "",
						phone_number: p.phone_number || "",
						number: p.number || "",
						is_primary: p.is_primary,
						has_whatsapp: p.has_whatsapp,
					}))
				: undefined,
			email: email ?? undefined,
			address: address ? address : undefined,

			tourist_id: tourist_id ?? null,
			meal_preference: meal_preference ?? undefined,
			stay_preference: stay_preference ?? undefined,
			language_preference: language_preference ?? undefined,
			preferred_airport: preferred_airport
				? addLabelToAirport(preferred_airport)
				: undefined,
			preferred_flight_class: preferred_flight_class ?? "",
			marriage_anniversary: marriage_anniversary ?? "",
			is_smoker: is_smoker ?? null,
			tags: tags ?? [],
			passport_number: passport_number ?? "",
			passport_expiry_date: passport_expiry_date
				? parseDate(passport_expiry_date)
				: undefined,
			company_name: company_name ?? "",
			gst_number: gst_number ?? "",
			remarks: remarks ?? "",
		}
	}, [propInitialValues])

	const validate = useMemo(() => {
		const emailValidator = Validator.string().email(
			"Please provide a valid email address"
		)
		const phoneValidator = Validator.array().when("name", ([Name], schema) => {
			if (Name) {
				return schema
					.of(PhoneNumberValidator("Phone Number", false))
					.min(1, "Please provide atleast one phone number")
			}
			return schema.of(PhoneNumberValidator("Phone Number", true))
		})

		const validationSchema = Validator.object().shape({
			type: Validator.string()
				.oneOf(["adult", "child"])
				.required("Tourist profile type is required"),
			salutation: Validator.mixed().nullable(),
			name: Validator.string()
				.required("Name field is required")
				.max(100, "Please use 100 or less characters for name"),
			dob: Validator.date().nullable(),
			nationality: Validator.mixed().nullable(),
			email: emailValidator,
			phone_numbers: phoneValidator,
			address: Validator.mixed().nullable(),
			meal_preference: Validator.object().required(
				"Please select meal preference"
			),
			stay_preference: Validator.object().required(
				"Please select stay preference"
			),
			language_preference: Validator.object().nullable(),
			preferred_airport: Validator.object().nullable(),
			preferred_flight_class: Validator.string().nullable(),
			marriage_anniversary: Validator.string().nullable(),
			is_smoker: Validator.string().nullable(),
			tags: Validator.array().of(Validator.mixed()).nullable(),
			passport_number: Validator.string().nullable(),
			passport_expiry_date: Validator.date().nullable(),
			company_name: Validator.string().nullable(),
			gst_number: Validator.string().nullable(),
			remarks: Validator.string().nullable(),
		})
		return validateFormValues(validationSchema)
	}, [])

	return (
		<Form<TTouristProfileFormData>
			initialValues={computedInitialValues}
			enableReinitialize
			validate={validate}
			mutators={{ ...arrayMutators }}
			onSubmit={withServerErrors(async (touristProfile) => {
				const {
					type,
					nationality,
					address,
					dob,
					phone_numbers,
					meal_preference,
					stay_preference,
					language_preference,
					preferred_airport,
					is_smoker,
					tags,
					passport_expiry_date,
					gst_number,
					...rest
				} = touristProfile

				const formattedAddress = address?.location
					? {
							...transformAddressToStorableValue(address),
							addressing_name: rest.name,
							billing_details: address.billing_details,
						}
					: undefined
				const data = {
					...rest,
					type: type ? type : undefined,
					nationality_id: nationality ? nationality.id : undefined,
					address: formattedAddress,
					dob: dob ? formatDate(dob, "YYYY-MM-DD") : undefined,
					phone_numbers: (phone_numbers || []).filter((p) => p.number),

					meal_preference: meal_preference?.name,
					stay_preference: stay_preference?.name,
					language_preference: language_preference?.name,
					preferred_airport_id: preferred_airport?.id,
					is_smoker: is_smoker === "Yes" ? 1 : is_smoker === "No" ? 0 : null,
					tags: tags?.map((tag) => tag?.name),
					passport_expiry_date: passport_expiry_date
						? formatDate(passport_expiry_date, "YYYY-MM-DD")
						: undefined,

					gst_number: address?.location ? gst_number : undefined,
				}

				return onSubmit(data)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						{!hideBasicInfo ? (
							<>
								<TouristProfileBasicInfoItem
									touristProfile={computedInitialValues}
								/>
								<Divider sm />
							</>
						) : null}
						<TouristProfileAdditionalInfoItem />
						<Divider sm />
						<Grid>
							<Col sm={12} md={{ offset: 3, span: 9 }}>
								<Stack gap="4" paddingLeft={{ md: "4" }}>
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Saving..." : "Save Details"}
										</Button>
										{onCancel ? (
											<Button
												onClick={onCancel}
												level="tertiary"
												disabled={submitting}
											>
												Cancel
											</Button>
										) : null}
									</Inline>
								</Stack>
							</Col>
						</Grid>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export const TouristProfileBasicInfoItem = memo(
	function TouristProfileBasicInfoItem({
		touristProfile,
	}: {
		touristProfile?: TTouristProfileFormData
	}) {
		const form = useForm<TTouristProfileFormData>()

		const handleTypeChange = useCallback(
			(e: React.ChangeEvent<HTMLSelectElement>) => {
				const value = e.currentTarget.value as "adult" | "child"

				form.batch(() => {
					form.change("type", value)
					form.change("salutation", undefined)
				})
			},
			[form]
		)

		return (
			<>
				<Grid gap="4">
					<Col sm={12} md={3}>
						<Stack gap="1">
							<Heading as="h3">Personal Info</Heading>
							<Text color="muted">
								Basic traveler details and identity information.
							</Text>
						</Stack>
					</Col>
					<Col>
						<Stack gap="6">
							<Grid>
								<Col sm={12}>
									<SelectInputField
										style={{ width: 250 }}
										name="type"
										label="Type"
										disabled
										required
										placeholder="Select Type"
										onChange={handleTypeChange}
									>
										<option value="adult">Adult</option>
										<option value="child">Child</option>
									</SelectInputField>
									<Inline alignItems="center" gap="1" padding="1">
										<Icons.Info color="muted" fontSize="xs" />
										<Text fontSize="xs" color="muted">
											Type cannot be changes once the tourist profile is
											created.
										</Text>
									</Inline>
								</Col>
							</Grid>
							<Grid>
								<Col sm={12} md={4}>
									<GetFieldValue<TTouristProfileFormData["type"]> name="type">
										{({ value: type }) => (
											<SelectField
												select={Select}
												name="salutation"
												label="Salutation"
												placeholder={
													type === "adult" ? "e.g. Mr." : "e.g. Master"
												}
												options={
													type === "adult" ? adultSalutations : childSalutations
												}
											/>
										)}
									</GetFieldValue>
								</Col>

								<Col sm={12} md={6}>
									<TextInputField
										name="name"
										label="Name"
										required
										placeholder="e.g. Guest Name"
										maxWidth={"sm"}
									/>
								</Col>
							</Grid>
							<Grid>
								<Col sm={12} md={4}>
									<DatePickerField
										name="dob"
										label="Date of Birth"
										dateFormat="DD MMM, YYYY"
										placeholder="e.g. 12/01/1990"
										max={new Date()}
									/>
								</Col>

								<Col sm={12} md={6}>
									<SelectField
										select={SelectWorldCountries}
										name="nationality"
										label="Nationality"
										required
										placeholder="e.g. Indian"
										fetchOnMount
										cacheKey={`profiles-nationality-${touristProfile?.id}`}
										labelKey="nationality"
										optionRenderer={({ option }) => <Text>{option.name}</Text>}
									/>
								</Col>
							</Grid>
						</Stack>
					</Col>
				</Grid>
				<Divider sm />
				<Grid gap="4">
					<Col sm={12} md={3}>
						<Stack gap="1">
							<Heading as="h3">Contact Info</Heading>
							<Text color="muted">Communication and location details.</Text>
						</Stack>
					</Col>
					<Col>
						<Stack gap="6">
							<Grid>
								<Col sm={12}>
									<Inline gap="2" flexWrap="wrap">
										<PhoneInputField
											name={`phone_numbers`}
											label="Phone Number(s)"
											multi
											required
											fullWidth
											showWhatsapp
										/>
										<GetFieldValue name={`email`}>
											{({ value: email }) => (
												<Component initialState={email ?? false}>
													{({ state, setState }) => (
														<>
															{state ? (
																<TextInputField
																	name={`email`}
																	label="Email"
																	type="email"
																	placeholder="e.g. profile@example.com"
																/>
															) : (
																<Stack gap="1">
																	<Box
																		display={{
																			sm: "block",
																		}}
																	>
																		&nbsp;
																	</Box>
																	<Box>
																		<Button
																			onClick={() => setState(true)}
																			title="Click to add Email Address"
																		>
																			<Icons.Mail />
																		</Button>
																	</Box>
																</Stack>
															)}
														</>
													)}
												</Component>
											)}
										</GetFieldValue>
									</Inline>
								</Col>
							</Grid>
							<Grid>
								<Col sm={12}>
									<AddressInputField
										name="address"
										label="Origin City/State"
										onlyLocation
										hidePreviewForLocation
									/>
								</Col>
							</Grid>
						</Stack>
					</Col>
				</Grid>
			</>
		)
	}
)

export const TouristProfileAdditionalInfoItem = () => {
	return (
		<>
			<Grid gap="4">
				<Col sm={12} md={3}>
					<Stack gap="1">
						<Heading as="h3">Travel Preferences</Heading>
						<Text color="muted">
							Help us personalize the travel experience based on traveler's
							preferences.
						</Text>
					</Stack>
				</Col>
				<Col>
					<Stack gap="6">
						<Grid>
							<Col xs={12} sm={4}>
								<SelectField
									name="meal_preference"
									select={SelectPreferences}
									label="Meal Pref."
									type="meal"
									placeholder="Type to search..."
									fetchOnMount
								/>
							</Col>
							<Col xs={12} sm={4}>
								<SelectField
									name="stay_preference"
									select={SelectPreferences}
									label="Preferred Stay"
									type="stay"
									placeholder="Type to search..."
									fetchOnMount
								/>
							</Col>
							<Col xs={12} sm={4}>
								<SelectField
									name="language_preference"
									select={SelectPreferences}
									label="Language Pref."
									secondaryLabel="optional"
									type="language"
									placeholder="Type to search..."
									fetchOnMount
								/>
							</Col>
						</Grid>
						<Grid>
							<Col xs={12} sm={6}>
								<SelectField
									select={SelectAirports}
									name="preferred_airport"
									label="Preferred Airport"
									secondaryLabel="optional"
									creatable={true}
								/>
							</Col>
							<Col xs={12} sm={6}>
								<SelectField
									select={Select}
									name="preferred_flight_class"
									label="Flight Class Preference"
									secondaryLabel="optional"
									options={FLIGHT_CATEGORY_CLASSES}
								/>
							</Col>
						</Grid>
					</Stack>
				</Col>
			</Grid>
			<Divider sm />
			<Grid gap="4">
				<Col sm={12} md={3}>
					<Stack gap="1">
						<Heading as="h3">Other Preferences</Heading>
						<Text color="muted">
							Store preferred other details for faster quote creation.
						</Text>
					</Stack>
				</Col>
				<Col>
					<Stack gap="6">
						<Grid>
							<Col xs={12} sm={4}>
								<DatePickerField
									name="marriage_anniversary"
									label="Anniversary Date"
									secondaryLabel="optional"
								/>
							</Col>
							<Col xs={12} sm={4}>
								<SelectField
									select={Select}
									name="is_smoker"
									label="Smoking"
									secondaryLabel="optional"
									options={SMOKING_PREFERENCES}
								/>
							</Col>
							<Col xs={12} sm={4}>
								<SelectField
									name="tags"
									select={SelectTags}
									label="Tags"
									secondaryLabel="optional"
									type="tourist-profile"
									multiple
									placeholder="Type to search..."
									fetchOnMount
								/>
							</Col>
						</Grid>
						<Grid>
							<Col xs={12} sm={6}>
								<Box>
									<TextInputField
										name="passport_number"
										type="text"
										label="Passport Number"
										secondaryLabel="optional"
										placeholder="e.g. A1234567"
										maxWidth="sm"
									/>
								</Box>
							</Col>
							<Col xs={12} sm={6}>
								<Box>
									<DatePickerField
										name="passport_expiry_date"
										label="Passport Expiry Date"
										secondaryLabel="optional"
									/>
								</Box>
							</Col>
						</Grid>
					</Stack>
				</Col>
			</Grid>
			<Divider sm />
			<Grid gap="4">
				<Col sm={12} md={3}>
					<Stack gap="1">
						<Heading as="h3">Billing & Business Details</Heading>
						<Text color="muted">
							Save billing information for future quotations and invoices.
						</Text>
					</Stack>
				</Col>
				<Col>
					<Stack gap="6">
						<Grid>
							<Col xs={12} sm={4}>
								<Box>
									<TextInputField
										name="company_name"
										type="text"
										label="Company Name"
										secondaryLabel="optional"
										placeholder="e.g. ABC Technologies pvt. ltd."
										maxWidth="sm"
									/>
								</Box>
							</Col>
							<Col xs={12} sm={4}>
								<Box>
									<TextInputField
										name="gst_number"
										type="text"
										label="Tax Details"
										secondaryLabel="optional"
										placeholder="e.g. 27ABCDE1234F1Z5"
										maxWidth="sm"
										help="e.g. 27ABCDE1234F1Z5"
									/>
								</Box>
							</Col>
							<Col xs={12} sm={4}>
								<Box>
									<TextAreaInputField
										name="remarks"
										label="Remarks"
										secondaryLabel="optional"
										placeholder="Additional notes about the tourist profile"
										maxLength={1000}
									/>
								</Box>
							</Col>
						</Grid>
					</Stack>
				</Col>
			</Grid>
		</>
	)
}
